import axios from 'axios';
import * as Sentry from '@sentry/react';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// ── Auth token interceptor ──────────────────────────────────

type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter | null = null;

export function setTokenGetter(getter: TokenGetter) {
  _getToken = getter;
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!_getToken) return {};
  const token = await _getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

api.interceptors.request.use(async (config) => {
  if (_getToken) {
    const token = await _getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Debounce auth:unauthorized
let _unauthorizedTimer: ReturnType<typeof setTimeout> | null = null;
function _dispatchUnauthorized() {
  if (!_unauthorizedTimer) {
    _unauthorizedTimer = setTimeout(() => {
      _unauthorizedTimer = null;
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }, 100);
  }
}

// ── Circuit breaker ──────────────────────────────────────────
const _CB_WINDOW_MS = 30_000;
const _CB_THRESHOLD = 5;
const _CB_COOLDOWN_MS = 15_000;

let _cbErrors: number[] = [];
let _cbOpenUntil = 0;

function _cbRecordError() {
  const now = Date.now();
  _cbErrors.push(now);
  _cbErrors = _cbErrors.filter(t => now - t < _CB_WINDOW_MS);
  if (_cbErrors.length >= _CB_THRESHOLD) {
    _cbOpenUntil = now + _CB_COOLDOWN_MS;
    _cbErrors = [];
    Sentry.captureMessage('API circuit breaker tripped', { level: 'error', tags: { source: 'circuit_breaker' } });
  }
}

function _cbIsOpen(): boolean {
  return Date.now() < _cbOpenUntil;
}

function _normalizeApiRoute(rawUrl: unknown): string {
  if (typeof rawUrl !== 'string' || !rawUrl) return 'unknown';
  const path = rawUrl.split('?')[0] || rawUrl;
  return path.replace(/\/\d+(?=\/|$)/g, '/{id}');
}

function _reportInternalApiFailure(error: any) {
  if (axios.isCancel(error)) return;
  const status = error.response?.status as number | undefined;
  // 405 indicates a real client/server route disagreement (contract drift).
  // 404 is too noisy in SPAs (deleted resources, stale links) — drop it.
  const shouldReport = !error.response || (status !== undefined && (status >= 500 || status === 405));
  if (!shouldReport) return;

  const route = _normalizeApiRoute(error.config?.url);
  const method = String(error.config?.method || 'GET').toUpperCase();
  const failureKind = !error.response ? 'network' : status && status >= 500 ? 'frontend_api_5xx' : 'contract_drift';
  Sentry.captureMessage('Sabrina frontend API failure', {
    level: status && status >= 500 ? 'error' : 'warning',
    tags: {
      source_app: 'sabrina-frontend',
      target_app: 'sabrina',
      route,
      method,
      status_code: status ?? 'network',
      failure_kind: failureKind,
    },
  });
}

api.interceptors.request.use((config) => {
  const url = config.url || '';
  if (_cbIsOpen() && !url.includes('/health') && !url.includes('/auth/')) {
    return Promise.reject(new axios.CanceledError('API circuit breaker open'));
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status >= 500 || (!error.response && !axios.isCancel(error))) {
      _cbRecordError();
    }
    if (error.response?.status === 401 && _getToken && !error.config._retried) {
      try {
        const newToken = await _getToken();
        if (newToken) {
          error.config._retried = true;
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        }
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, { tags: { source: '401_token_retry' } });
      }
      _dispatchUnauthorized();
    } else if (error.response?.status === 401) {
      _dispatchUnauthorized();
    }
    if (error.response?.status === 403 && error.response?.data?.detail === 'Account is deactivated') {
      window.dispatchEvent(new CustomEvent('auth:deactivated'));
    }
    _reportInternalApiFailure(error);
    return Promise.reject(error);
  },
);

// ── API functions ────────────────────────────────────────────

export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  role: string;
  active: boolean;
}

export function getCurrentUser() {
  return api.get<CurrentUser>('/auth/me').then(r => r.data);
}

export function getKimaiStatus() {
  return api.get('/kimai/status').then(r => r.data);
}

export function relinkKimaiByEmail() {
  return api.post('/kimai/relink-by-email').then(r => r.data);
}

export function syncKimaiEmployees() {
  return api.post('/kimai/sync-employees').then(r => r.data);
}

export function getDashboardSummary() {
  return api.get('/dashboard/summary').then(r => r.data);
}

// Bills
export function getBills(params?: { status?: string; vendor_id?: number; needs_review?: boolean; limit?: number; offset?: number }) {
  return api.get('/bills/', { params }).then(r => r.data);
}

export function getBill(id: number) {
  return api.get(`/bills/${id}`).then(r => r.data);
}

export function getBillStats() {
  return api.get('/bills/stats').then(r => r.data);
}

export function getReviewQueue(params?: { limit?: number; offset?: number }) {
  return api.get('/bills/review-queue', { params }).then(r => r.data);
}

export function createBill(data: Record<string, unknown>) {
  return api.post('/bills/', data).then(r => r.data);
}

export function uploadBill(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/bills/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
}

export function updateBill(id: number, data: Record<string, unknown>) {
  return api.patch(`/bills/${id}`, data).then(r => r.data);
}

export function reviewBill(id: number, data: Record<string, unknown>) {
  return api.post(`/bills/${id}/review`, data).then(r => r.data);
}

export function approveBill(id: number) {
  return api.post(`/bills/${id}/approve`).then(r => r.data);
}

export function rejectBill(id: number, reason?: string) {
  return api.post(`/bills/${id}/reject`, null, { params: { reason } }).then(r => r.data);
}

export function reExtractBill(id: number) {
  return api.post(`/bills/${id}/re-extract`).then(r => r.data);
}

export function getBillDocument(id: number) {
  return api.get(`/bills/${id}/document`, { responseType: 'blob' }).then(r => r.data);
}

// Context Rules
export function getContextRules() {
  return api.get('/bills/context-rules/').then(r => r.data);
}

export function createContextRule(data: Record<string, unknown>) {
  return api.post('/bills/context-rules/', data).then(r => r.data);
}

export function deleteContextRule(id: number) {
  return api.delete(`/bills/context-rules/${id}`).then(r => r.data);
}

// Vendors
export function getVendors(params?: { active?: boolean; category?: string; q?: string }) {
  return api.get('/vendors/', { params }).then(r => r.data);
}

export function createVendor(data: Record<string, unknown>) {
  return api.post('/vendors/', data).then(r => r.data);
}

export function updateVendor(id: number, data: Record<string, unknown>) {
  return api.patch(`/vendors/${id}`, data).then(r => r.data);
}

// CLE
export function getCleRequirements() {
  return api.get('/cle/requirements').then(r => r.data);
}

export function updateCleRequirement(id: number, data: Record<string, unknown>) {
  return api.patch(`/cle/requirements/${id}`, data).then(r => r.data);
}

export function getCleCompliance(params?: { user_id?: number }) {
  return api.get('/cle/compliance', { params }).then(r => r.data);
}

export function getCleCredits(params?: { user_id?: number; state?: string }) {
  return api.get('/cle/credits', { params }).then(r => r.data);
}

export function createCleCredit(data: Record<string, unknown>) {
  return api.post('/cle/credits', data).then(r => r.data);
}

export function updateCleCredit(id: number, data: Record<string, unknown>) {
  return api.patch(`/cle/credits/${id}`, data).then(r => r.data);
}

export function deleteCleCredit(id: number) {
  return api.delete(`/cle/credits/${id}`).then(r => r.data);
}

export function bulkImportCleCredits(data: { credits: Record<string, unknown>[] }) {
  return api.post('/cle/credits/bulk-import', data).then(r => r.data);
}

// CLE Courses (shared)
export function getCleCourses(params?: { q?: string }) {
  return api.get('/cle/courses', { params }).then(r => r.data);
}

export function getCleCourse(id: number) {
  return api.get(`/cle/courses/${id}`).then(r => r.data);
}

export function createCleCourse(data: Record<string, unknown>) {
  return api.post('/cle/courses', data).then(r => r.data);
}

export function updateCleCourse(id: number, data: Record<string, unknown>) {
  return api.patch(`/cle/courses/${id}`, data).then(r => r.data);
}

export function deleteCleCourse(id: number) {
  return api.delete(`/cle/courses/${id}`).then(r => r.data);
}

// CLE Documents
export function uploadCourseDocument(courseId: number, file: File, docType: string, description?: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('doc_type', docType);
  if (description) formData.append('description', description);
  return api.post(`/cle/courses/${courseId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
}

export function uploadCertificate(creditId: number, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/cle/credits/${creditId}/certificate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
}

export function deleteCleDocument(id: number) {
  return api.delete(`/cle/documents/${id}`).then(r => r.data);
}

export function getCleDocumentUrl(id: number) {
  return `/api/cle/documents/${id}/download`;
}

// CLE Screenshot Audit
export function screenshotAudit(file: File, state: string, userId: number) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('state', state);
  formData.append('user_id', String(userId));
  return api.post('/cle/screenshot-audit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000, // 3 min — PDF transcript analysis can be slow
  }).then(r => r.data);
}

export function importAuditResults(data: { user_id: number; state: string; credits: Record<string, unknown>[] }) {
  return api.post('/cle/screenshot-audit/import', data).then(r => r.data);
}

// Employees
export function getEmployees() {
  return api.get('/employees/').then(r => r.data);
}

export function createEmployee(data: Record<string, unknown>) {
  return api.post('/employees/', data).then(r => r.data);
}

export function updateEmployee(id: number, data: Record<string, unknown>) {
  return api.patch(`/employees/${id}`, data).then(r => r.data);
}

export function resignEmployee(id: number, data: Record<string, unknown>) {
  return api.post(`/employees/${id}/resign`, data).then(r => r.data);
}

export function reactivateEmployee(id: number) {
  return api.post(`/employees/${id}/reactivate`).then(r => r.data);
}

export function getMyEmployee() {
  return api.get('/employees/me').then(r => r.data);
}

// Subscriptions
export function getSubscriptions() {
  return api.get('/subscriptions/').then(r => r.data);
}

export function createSubscription(data: Record<string, unknown>) {
  return api.post('/subscriptions/', data).then(r => r.data);
}

export function updateSubscription(id: number, data: Record<string, unknown>) {
  return api.patch(`/subscriptions/${id}`, data).then(r => r.data);
}

export function deleteSubscription(id: number) {
  return api.delete(`/subscriptions/${id}`).then(r => r.data);
}

// Insurance
export function getInsurancePolicies() {
  return api.get('/subscriptions/insurance').then(r => r.data);
}

export function createInsurancePolicy(data: Record<string, unknown>) {
  return api.post('/subscriptions/insurance', data).then(r => r.data);
}

export function updateInsurancePolicy(id: number, data: Record<string, unknown>) {
  return api.patch(`/subscriptions/insurance/${id}`, data).then(r => r.data);
}

export function deleteInsurancePolicy(id: number) {
  return api.delete(`/subscriptions/insurance/${id}`).then(r => r.data);
}

// Leases
export function getLeases() {
  return api.get('/subscriptions/leases').then(r => r.data);
}

export function createLease(data: Record<string, unknown>) {
  return api.post('/subscriptions/leases', data).then(r => r.data);
}

export function updateLease(id: number, data: Record<string, unknown>) {
  return api.patch(`/subscriptions/leases/${id}`, data).then(r => r.data);
}

export function deleteLease(id: number) {
  return api.delete(`/subscriptions/leases/${id}`).then(r => r.data);
}

// Users
export function getUsers() {
  return api.get('/users/').then(r => r.data);
}

// PTO
export function getPtoBalance() {
  return api.get('/pto/balance').then(r => r.data);
}

export function getPtoRequests(params?: { status?: string; year?: number }) {
  return api.get('/pto/requests', { params }).then(r => r.data);
}

export function createPtoRequest(data: { request_type: string; start_date: string; end_date: string; hours: number; notes?: string }) {
  return api.post('/pto/requests', data).then(r => r.data);
}

export function reviewPtoRequest(id: number, data: { status: string; review_notes?: string }) {
  return api.patch(`/pto/requests/${id}/review`, data).then(r => r.data);
}

export function cancelPtoRequest(id: number) {
  return api.delete(`/pto/requests/${id}`).then(r => r.data);
}

export function getLeaveBalances(params?: { active_only?: boolean }) {
  return api.get('/pto/admin/balances', { params }).then(r => r.data);
}

export function getEmployeeLeave(employeeId: number, asOf?: string) {
  return api.get(`/pto/admin/employees/${employeeId}/leave`, {
    params: asOf ? { as_of: asOf } : undefined,
  }).then(r => r.data);
}

export function getLeaveLedger(params?: { employee_id?: number; leave_type?: string; limit?: number; offset?: number }) {
  return api.get('/pto/admin/ledger', { params }).then(r => r.data);
}

export function updateEmployeeLeave(id: number, data: Record<string, unknown>) {
  return api.patch(`/pto/admin/employees/${id}/leave`, data).then(r => r.data);
}

export function runLeaveAccrual(data: { period_start: string; period_end: string; employee_id?: number; dry_run?: boolean }) {
  return api.post('/pto/admin/accrual/run', data).then(r => r.data);
}

// Holidays
export function getHolidays(params?: { year?: number }) {
  return api.get('/holidays/', { params }).then(r => r.data);
}

export function getUpcomingHolidays(limit = 5) {
  return api.get('/holidays/upcoming', { params: { limit } }).then(r => r.data);
}

export function createHoliday(data: { name: string; date: string; observed_date?: string; paid?: boolean }) {
  return api.post('/holidays/', data).then(r => r.data);
}

export function updateHoliday(id: number, data: Record<string, unknown>) {
  return api.patch(`/holidays/${id}`, data).then(r => r.data);
}

export function deleteHoliday(id: number) {
  return api.delete(`/holidays/${id}`).then(r => r.data);
}

// Payroll
export function getPayrollCurrentPeriod() {
  return api.get('/payroll/current-period').then(r => r.data);
}

export function getPayrollPeriod(offset = 0) {
  return api.get('/payroll/period', { params: { offset } }).then(r => r.data);
}

export function getPayrollDashboard(periodStart?: string, periodEnd?: string) {
  return api.get('/payroll/dashboard', { params: { period_start: periodStart, period_end: periodEnd } }).then(r => r.data);
}

export function getPayrollRuns() {
  return api.get('/payroll/runs').then(r => r.data);
}

export function triggerPayrollCheck(force = false, periodOffset = 0) {
  return api.post(`/payroll/runs/trigger?force=${force}&period_offset=${periodOffset}`).then(r => r.data);
}

export function resolvePayrollException(id: number, resolutionNotes: string) {
  return api.patch(`/payroll/exceptions/${id}/resolve`, { resolution_notes: resolutionNotes }).then(r => r.data);
}

export function sendPayrollReport(runId: number) {
  return api.post(`/payroll/report/${runId}/send`).then(r => r.data);
}

export const approvePayrollBonuses = (runId: number, approvalToken: string) =>
  api.post(`/payroll/runs/${runId}/bonuses/approve`, { approval_token: approvalToken }).then(r => r.data);

export const resolvePayrollBonusElsewhere = (runId: number, reason: string) =>
  api.post(`/payroll/runs/${runId}/bonuses/resolve-elsewhere`, { reason }).then(r => r.data);

export const retryPayrollBonusAcknowledgment = (runId: number) =>
  api.post(`/payroll/runs/${runId}/bonuses/retry-ack`).then(r => r.data);

export const reconcilePayrollBonusEmail = (runId: number, sent: boolean, reason: string) =>
  api.post(`/payroll/runs/${runId}/bonuses/reconcile`, { sent, reason }).then(r => r.data);

export const retryPayrollBonusRelease = (runId: number) =>
  api.post(`/payroll/runs/${runId}/bonuses/retry-release`).then(r => r.data);

export interface WorkSchedule {
  weekdays: number[];
  daily_hours: number;
}

export interface PayrollReviewLeave {
  id: number;
  employee_id: number;
  employee_name: string;
  request_type: string;
  start_date: string;
  end_date: string;
  hours: number;
  notes: string | null;
  overdue: boolean;
}

export interface PayrollReviewException {
  id: number;
  employee_id: number;
  employee_name: string;
  exception_type: string;
  ai_explanation: string | null;
  expected_hours: number | null;
  actual_hours: number | null;
}

export interface PayrollReviewApproval {
  employee_id: number;
  employee_name: string;
  manager_approved_at: string | null;
  locked_at: string | null;
  default_schedule: WorkSchedule;
  effective_schedule: WorkSchedule;
  has_schedule_override: boolean;
}

export interface PayrollBonusEmployee {
  employee_id: number;
  employee_name: string;
  email: string;
  call_case_count: number;
  call_case_cents: number;
  correction_cents: number;
  total_cents: number;
}

export interface PayrollBonusReview {
  enabled: boolean;
  available?: boolean;
  approved?: boolean;
  email_attempted?: boolean;
  resolution_required?: boolean;
  state?: 'unapproved' | 'unavailable' | 'prepared' | 'sending' | 'sent_ack_pending' | 'reported' | 'needs_reconciliation' | 'release_pending' | 'reservation_conflict';
  item_count?: number;
  total_cents?: number;
  employees?: PayrollBonusEmployee[];
  approved_at?: string | null;
  approval_token?: string | null;
  error?: string | null;
}

export interface PayrollReview {
  run_id: number;
  period_start: string;
  period_end: string;
  employees: Array<Record<string, unknown>>;
  pending_leave: PayrollReviewLeave[];
  unresolved_exceptions: PayrollReviewException[];
  approvals: PayrollReviewApproval[];
  blockers: Array<{ code: string; count: number }>;
  eligible: boolean;
  context_source: 'live_kimai' | 'saved_run';
  bonus_payments?: PayrollBonusReview;
}

export const getPayrollReview = (runId: number) =>
  api.get<PayrollReview>(`/payroll/runs/${runId}/review`).then(r => r.data);

export const savePayrollScheduleOverride = (runId: number, employeeId: number, schedule: WorkSchedule) =>
  api.put(`/payroll/runs/${runId}/employees/${employeeId}/schedule`, schedule).then(r => r.data);

// ── Timeclock Admin ──────────────────────────────────────────

export const getTimeclockEmployees = () =>
  api.get('/timeclock/admin/employees').then(r => r.data);

export const getTimeclockEntries = (employeeId: number, begin: string, end: string) =>
  api.get(`/timeclock/admin/entries/${employeeId}`, { params: { begin, end } }).then(r => r.data);

export const adjustTimeclockEntry = (timesheetId: number, data: { new_begin?: string; new_end?: string; reason: string }) =>
  api.post(`/timeclock/admin/entries/${timesheetId}/adjust`, data).then(r => r.data);

export const closeOpenShift = (timesheetId: number, data: { end_time?: string; reason: string }) =>
  api.post(`/timeclock/admin/entries/${timesheetId}/close-open-shift`, data).then(r => r.data);

export const createTimeclockEntry = (data: { employee_id: number; begin: string; end: string; reason?: string; punch_type?: string }) =>
  api.post('/timeclock/admin/entries/create', data).then(r => r.data);

export const deleteTimeclockEntry = (timesheetId: number, reason?: string) =>
  api.delete(`/timeclock/admin/entries/${timesheetId}`, { params: { reason: reason || '' } }).then(r => r.data);

export const getTimeclockEditHistory = (employeeId: number) =>
  api.get(`/timeclock/admin/edit-history/${employeeId}`).then(r => r.data);

export const getTimeclockExceptions = () =>
  api.get('/timeclock/admin/exceptions').then(r => r.data);

export const getPayPeriodApprovals = (periodStart: string) =>
  api.get(`/timeclock/admin/pay-period/${periodStart}/approvals`).then(r => r.data);

export const approvePayPeriod = (periodStart: string, employeeId: number) =>
  api.post(`/timeclock/admin/pay-period/${periodStart}/approve/${employeeId}`).then(r => r.data);

export const lockPayPeriod = (periodStart: string, employeeId: number) =>
  api.post(`/timeclock/admin/pay-period/${periodStart}/lock/${employeeId}`).then(r => r.data);

export const unlockPayPeriod = (periodStart: string, employeeId: number, reason: string) =>
  api.post(`/timeclock/admin/pay-period/${periodStart}/unlock/${employeeId}`, { reason }).then(r => r.data);

// ── Advertising Intelligence ─────────────────────────────────

export interface AdDashboardParams {
  start_date?: string;
  end_date?: string;
  platform?: string;
  practice_area?: string;
  market?: string;
  group_by?: 'campaign' | 'platform' | 'practice_area' | 'market';
}

export const getAdDashboard = (params?: AdDashboardParams) =>
  api.get('/advertising/dashboard', { params }).then(r => r.data);

export const refreshAdMetrics = () =>
  api.post('/advertising/refresh').then(r => r.data);

export const getAdDigests = (limit = 10) =>
  api.get('/advertising/digests', { params: { limit } }).then(r => r.data);

export const generateAdDigest = (start_date: string, end_date: string) =>
  api.post('/advertising/digests/generate', { start_date, end_date }).then(r => r.data);

export const getAdValidation = (days = 30) =>
  api.get('/advertising/validation', { params: { days } }).then(r => r.data);

export const getAdCampaigns = () =>
  api.get('/advertising/campaigns').then(r => r.data);

export const classifyAdCampaign = (data: { platform: string; campaign_id: string; practice_area?: string; market?: string }) =>
  api.post('/advertising/campaigns/classify', data).then(r => r.data);

export interface AdFunnelParams {
  start_date?: string;
  end_date?: string;
  platform?: string;
  practice_area?: string;
  market?: string;
  group_by?: 'lead_source' | 'practice_area' | 'market';
}

export const getAdFunnel = (params?: AdFunnelParams) =>
  api.get('/advertising/funnel', { params }).then(r => r.data);

// ── RC Call Archive ───────────────────────────────────────────

export interface ArchivedCall {
  id: number;
  rc_call_id: string;
  direction: string;
  result: string | null;
  from_number: string | null;
  from_name: string | null;
  from_extension: string | null;
  to_number: string | null;
  to_name: string | null;
  to_extension: string | null;
  duration_seconds: number | null;
  started_at: string | null;
  has_recording: boolean;
  recording_available: boolean;
}

export interface CallSearchResult {
  items: ArchivedCall[];
  total: number;
  limit: number;
  offset: number;
}

export const searchCalls = (params: {
  q?: string;
  direction?: string;
  extension?: string;
  date_from?: string;
  date_to?: string;
  has_recording?: boolean;
  limit?: number;
  offset?: number;
}) => api.get<CallSearchResult>('/rc-archive/calls', { params }).then(r => r.data);

export interface ExtensionInfo {
  extension: string;
  name: string | null;
  total_calls: number;
}

export interface CallReport {
  total_calls: number;
  inbound: number;
  outbound: number;
  connected: number;
  missed: number;
  avg_duration_seconds: number;
  total_duration_seconds: number;
  daily_breakdown: { date: string; count: number }[];
}

export const getExtensions = () =>
  api.get<ExtensionInfo[]>('/rc-archive/extensions').then(r => r.data);

export const getCallReport = (params: {
  extension?: string;
  date_from?: string;
  date_to?: string;
}) => api.get<CallReport>('/rc-archive/report', { params }).then(r => r.data);

export const getRecordingUrl = (callId: number) => `/api/rc-archive/calls/${callId}/recording`;

export const triggerRcSync = () => api.post('/rc-archive/sync').then(r => r.data);

export const triggerRcBackfill = () => api.post('/rc-archive/backfill').then(r => r.data);

// ── RC Messages & DND ───────────────────────────────────────

export interface ArchivedMessage {
  id: number;
  rc_message_id: string;
  message_type: string;
  direction: string;
  from_number: string | null;
  from_name: string | null;
  from_extension: string | null;
  to_numbers: string | null;
  to_name: string | null;
  subject: string | null;
  read_status: string | null;
  created_time: string | null;
}

export interface MessageSearchResult {
  items: ArchivedMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface DndExtensionStats {
  extension_id: string;
  name: string | null;
  total_dnd_minutes: number;
  total_missed_during_dnd: number;
  dnd_session_count: number;
  total_snapshots: number;
  dnd_snapshots: number;
  dnd_sessions: { start: string; end: string | null; last_seen: string; dnd_status: string; missed_calls: number; duration_minutes: number }[];
}

export interface DndReport {
  extensions: DndExtensionStats[];
}

export const searchMessages = (params: {
  q?: string;
  message_type?: string;
  direction?: string;
  date_from?: string;
  date_to?: string;
  extension?: string;
  limit?: number;
  offset?: number;
}) => api.get<MessageSearchResult>('/rc-archive/messages', { params }).then(r => r.data);

export const getDndReport = (params: {
  date_from?: string;
  date_to?: string;
  extension?: string;
}) => api.get<DndReport>('/rc-archive/dnd-report', { params }).then(r => r.data);

export const triggerMessageBackfill = () => api.post('/rc-archive/backfill-messages').then(r => r.data);

// Bug reports (#sabrina bug reporter)
export interface FeedbackSubmission {
  title: string;
  body: string;
  page: string;
}

export interface FeedbackResult {
  id: number;
  title: string;
  status: string;
  /** Whether the report actually reached the bug list, not merely our database. */
  filed: boolean;
  github_issue_number: number | null;
  github_url: string | null;
}

export function submitFeedback(payload: FeedbackSubmission) {
  return api.post<FeedbackResult>('/feedback', payload).then(r => r.data);
}

export default api;
